// src/pages/PipelineCandidatDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { PhoneLink } from '../utils/helpers';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  ArrowLeft, Star, Edit2, Save, X, Plus, Trash2,
  Phone, Mail, MapPin, Linkedin, FileText, Clock,
  CheckCircle, MessageSquare, RefreshCw, Video, Calendar,
  AlertTriangle, Users, Briefcase, Upload, Download, ExternalLink,
} from 'lucide-react';
import { pipelineAPI, cvAPI, rtAPI } from '../services/api';
import TracabilityInfo from '../components/common/TracabilityInfo';
import CVImportBadge from '../components/common/CVImportBadge';


const STATUS_OPTIONS   = ['NOUVEAU','EN_COURS','CONTACTE','ENTRETIEN','PROPOSE','PLACE','STAFFE','ARCHIVE','REFUSE','NON_QUALIFIE','BLACK_LISTE'];
const PRIORITY_OPTIONS = ['HAUTE','NORMALE','BASSE'];
const ACTION_TYPES     = ['APPEL','EMAIL','ENTRETIEN','RETOUR_CLIENT','NOTE','RELANCE','AUTRE'];
const ACTION_STATUS    = ['A_FAIRE','FAIT','ANNULE'];


const STATUS_META = {
  NOUVEAU:'#FF4100', EN_COURS:'#F59E0B', CONTACTE:'#A78BFA', ENTRETIEN:'#06B6D4',
  PROPOSE:'#EA580C', PLACE:'#16A34A', STAFFE:'#16A34A', ARCHIVE:'var(--text-muted)',
  REFUSE:'#DC2626', NON_QUALIFIE:'#DC2626', BLACK_LISTE:'#111827',
};

const STATUS_LABELS = {
  NOUVEAU:'Nouveau', EN_COURS:'En cours', CONTACTE:'Contacté', ENTRETIEN:'Entretien',
  PROPOSE:'Proposé', PLACE:'Placé', STAFFE:'Staffé', ARCHIVE:'Archivé',
  REFUSE:'Refusé', NON_QUALIFIE:'Non qualifié', BLACK_LISTE:'Blacklisté',
};


export default function PipelineCandidatDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [entry,    setEntry]    = useState(null);
  const [actions,  setActions]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [editing,  setEditing]  = useState(false);
  const [form,     setForm]     = useState({});
  const [saving,   setSaving]   = useState(false);
  const [newAction,setNewAction]= useState({ action_type:'NOTE', title:'', description:'', status:'A_FAIRE', due_date:'', duration_minutes:60 });
  const [showActionForm, setShowActionForm] = useState(false);
  const [allUsers,    setAllUsers]    = useState([]);
  const [invitedUsers, setInvitedUsers] = useState([]);
  const [entDate,     setEntDate]     = useState('');
  const [entHour,     setEntHour]     = useState('09');
  const [entMin,      setEntMin]      = useState('00');
  const [documents,   setDocuments]   = useState([]);
  const [uploading,   setUploading]   = useState(false);
  const [docType,     setDocType]     = useState('CV');
  const [cvInfo,      setCvInfo]      = useState(null);
  const [rtReports,   setRtReports]   = useState([]);


  const fetchData = async () => {
    setLoading(true);
    try {
      const [entryRes, actionsRes, docsRes] = await Promise.all([
        api.get(`/candidate-pipeline/${id}`),
        api.get(`/candidate-pipeline/${id}/actions`),
        pipelineAPI.listDocuments(id).catch(() => ({ data: [] })),
      ]);
      setEntry(entryRes.data);
      setForm(entryRes.data);
      setActions(actionsRes.data || []);
      setDocuments(docsRes.data || []);

      const candidateId = entryRes.data?.candidate_id;
      if (candidateId) {
        cvAPI.listByCandidate(candidateId)
          .then(r => {
            const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
            const latest = list.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))[0];
            setCvInfo(latest || null);
          })
          .catch(() => setCvInfo(null));

        rtAPI.getReportsForCandidate(candidateId)
          .then(r => setRtReports(r.data || []))
          .catch(() => setRtReports([]));
      }
    } catch { toast.error('Profil introuvable'); navigate('/pipeline'); }
    finally { setLoading(false); }
  };

  const handleUploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', file.name);
      fd.append('document_type', docType);
      const res = await pipelineAPI.uploadDocument(id, fd);
      setDocuments(prev => [...prev, res.data]);
      toast.success('Document ajouté');
    } catch { toast.error('Erreur upload'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try {
      await pipelineAPI.deleteDocument(docId);
      setDocuments(prev => prev.filter(d => d.id !== docId));
      toast.success('Document supprimé');
    } catch { toast.error('Erreur suppression'); }
  };


  useEffect(() => { fetchData(); }, [id]);

  useEffect(() => {
    api.get('/auth/users?is_active=true').then(r => setAllUsers(r.data || [])).catch(() => {});
  }, []);


  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/candidate-pipeline/${id}`, {
        pipeline_status: form.pipeline_status,
        priority:        form.priority,
        internal_comment:form.internal_comment,
        client_feedback: form.client_feedback,
        availability:    form.availability,
        tjm:             form.tjm,
        profile_url:     form.profile_url,
      });
      setEntry(res.data);
      setEditing(false);
      toast.success('Profil mis à jour');
    } catch { toast.error('Erreur sauvegarde'); }
    finally { setSaving(false); }
  };


  const handleAddAction = async () => {
    if (!newAction.title) { toast.error('Titre obligatoire'); return; }
    if (newAction.action_type === 'ENTRETIEN' && !entDate) {
      toast.error('La date et l\'heure sont obligatoires pour un entretien');
      return;
    }
    const computedDueDate = entDate ? `${entDate}T${entHour}:${entMin}` : (newAction.due_date || undefined);
    try {
      const res = await api.post(`/candidate-pipeline/${id}/actions`, {
        ...newAction,
        pipeline_entry_id: id,
        due_date: computedDueDate,
        invited_users: invitedUsers,
      });
      setActions(prev => [res.data, ...prev]);
      setNewAction({ action_type:'NOTE', title:'', description:'', status:'A_FAIRE', due_date:'', duration_minutes:60 });
      setEntDate(''); setEntHour('09'); setEntMin('00');
      setInvitedUsers([]);
      setShowActionForm(false);
      if (newAction.action_type === 'ENTRETIEN' && res.data.teams_link) {
        toast.success('Entretien Teams créé et email envoyé au candidat');
      } else {
        toast.success('Action ajoutée');
      }
    } catch { toast.error('Erreur'); }
  };


  const handleDeleteAction = async (actionId) => {
    try {
      await api.delete(`/candidate-pipeline/actions/${actionId}`);
      setActions(prev => prev.filter(a => a.id !== actionId));
      toast.success('Action supprimée');
    } catch { toast.error('Erreur'); }
  };


  const toggleFav = async () => {
    try {
      const res = await api.put(`/candidate-pipeline/${id}`, { favorite: !entry.favorite });
      setEntry(res.data);
    } catch { toast.error('Erreur'); }
  };


  const handleRemove = async () => {
    if (!window.confirm('Retirer ce profil du pipeline ?')) return;
    try {
      await api.delete(`/candidate-pipeline/${id}`);
      toast.success('Profil retiré du pipeline');
      navigate('/pipeline');
    } catch { toast.error('Erreur'); }
  };


  if (loading) return (
    <div style={{ display:'flex', justifyContent:'center', padding:'48px 0', gap:10, color:'var(--text-muted)' }}>
      <div className="spinner"/> Chargement…
    </div>
  );
  if (!entry) return null;


  const statusColor = STATUS_META[entry.pipeline_status] || 'var(--text-muted)';
  const prioColor   = entry.priority === 'HAUTE' ? '#DC2626' : entry.priority === 'BASSE' ? 'var(--text-muted)' : '#F59E0B';


  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left" style={{ display:'flex', alignItems:'center', gap:12 }}>
          <button className="btn btn-ghost btn-icon" onClick={() => navigate('/pipeline')}><ArrowLeft size={18}/></button>
          <div>
            <h1 style={{ display:'flex', alignItems:'center', gap:10 }}>
              {entry.full_name}
              <button onClick={toggleFav} style={{ background:'none', border:'none', cursor:'pointer', padding:0 }}>
                <Star size={20} fill={entry.favorite ? '#F59E0B' : 'none'} color={entry.favorite ? '#F59E0B' : 'rgba(255,255,255,0.14)'}/>
              </button>
            </h1>
            <p>{entry.title}</p>
          </div>
        </div>
        <div className="page-header-actions">
          {!editing ? (
            <>
              <button className="btn btn-secondary" onClick={() => setEditing(true)}><Edit2 size={15}/> Statut</button>
              <button className="btn btn-secondary" onClick={() => navigate(`/pipeline/${id}/edit`)}><Edit2 size={15}/> Modifier profil</button>
              <button className="btn btn-danger" style={{ background:'rgba(239,68,68,0.12)', color:'#DC2626', border:'1px solid rgba(239,68,68,0.22)' }} onClick={handleRemove}>
                <Trash2 size={15}/> Retirer du pipe
              </button>
            </>
          ) : (
            <>
              <button className="btn btn-secondary" onClick={() => { setEditing(false); setForm(entry); }}><X size={15}/> Annuler</button>
              <button className="btn btn-primary" disabled={saving} onClick={handleSave}>
                {saving ? <div className="spinner" style={{ width:14, height:14 }}/> : <Save size={15}/>} Sauvegarder
              </button>
            </>
          )}
        </div>
      </div>

      {/* Traçabilité */}
      <div style={{ marginBottom: 16 }}>
        <TracabilityInfo
          createdAt={entry.added_at}
          createdById={entry.added_by_user_id}
          updatedAt={entry.updated_at}
        />
      </div>


      <div style={{ display:'grid', gridTemplateColumns:'1fr 380px', gap:20 }}>


        {/* Colonne gauche */}
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>


          {/* Infos profil */}
          <div className="card">
            <div className="card-header"><span className="card-title">Informations</span></div>
            <div style={{ padding:'0 0 4px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              {entry.civilite && <InfoRow icon={<Users size={14}/>} label="Civilité" value={entry.civilite}/>}
              {entry.candidate_type && <InfoRow icon={<Users size={14}/>} label="Type" value={entry.candidate_type}/>}
              {entry.email && <InfoRow icon={<Mail size={14}/>} label="Email" value={entry.email}/>}
              {entry.email_2 && <InfoRow icon={<Mail size={14}/>} label="Email 2" value={entry.email_2}/>}
              {entry.email_3 && <InfoRow icon={<Mail size={14}/>} label="Email 3" value={entry.email_3}/>}
              {entry.phone && <InfoRow icon={<Phone size={14}/>} label="Téléphone" value={<PhoneLink phone={entry.phone} style={{ color:'#FF4100' }}/>}/>}
              {entry.phone_2 && <InfoRow icon={<Phone size={14}/>} label="Téléphone 2" value={<PhoneLink phone={entry.phone_2} style={{ color:'#FF4100' }}/>}/>}
              {entry.phone_3 && <InfoRow icon={<Phone size={14}/>} label="Téléphone 3" value={<PhoneLink phone={entry.phone_3} style={{ color:'#FF4100' }}/>}/>}
              {(entry.address || entry.city) && <InfoRow icon={<MapPin size={14}/>} label="Adresse" value={[entry.address, entry.postal_code, entry.city, entry.country].filter(Boolean).join(', ')}/>}
              {entry.location && !entry.city && <InfoRow icon={<MapPin size={14}/>} label="Localisation" value={entry.location}/>}
              {entry.birth_date && <InfoRow icon={<Calendar size={14}/>} label="Date de naissance" value={entry.birth_date}/>}
              {entry.nationality && <InfoRow icon={<Users size={14}/>} label="Nationalité" value={entry.nationality}/>}
              {entry.family_situation && <InfoRow icon={<Users size={14}/>} label="Situation familiale" value={entry.family_situation}/>}
              {entry.experience && <InfoRow icon={<Clock size={14}/>} label="Expérience" value={entry.experience}/>}
              {entry.mobility && <InfoRow icon={<MapPin size={14}/>} label="Mobilité" value={entry.mobility}/>}
              {entry.contract_desired && <InfoRow icon={<AlertTriangle size={14}/>} label="Contrat souhaité" value={entry.contract_desired}/>}
              {entry.currency && <InfoRow icon={<AlertTriangle size={14}/>} label="Devise" value={entry.currency}/>}
              {entry.salary_current && <InfoRow icon={<AlertTriangle size={14}/>} label="Salaire actuel" value={entry.salary_current}/>}
              {(entry.salary_min || entry.salary_max) && <InfoRow icon={<AlertTriangle size={14}/>} label="Salaire souhaité" value={[entry.salary_min, entry.salary_max].filter(Boolean).join(' – ')}/>}
              {(entry.tjm_min || entry.tjm_max) && <InfoRow icon={<AlertTriangle size={14}/>} label="TJM souhaité" value={[entry.tjm_min, entry.tjm_max].filter(Boolean).join(' – ')}/>}
              {entry.linkedin_link && (
                <InfoRow icon={<Linkedin size={14}/>} label="LinkedIn" value={
                  <a href={entry.linkedin_link} target="_blank" rel="noreferrer" style={{ color:'#FF4100' }}>Voir profil</a>
                }/>
              )}
              {entry.profile_url && (
                <InfoRow icon={<ExternalLink size={14}/>} label="Lien Profil" value={
                  <a href={entry.profile_url} target="_blank" rel="noreferrer" style={{ color:'#FF4100' }}>{entry.profile_url}</a>
                }/>
              )}
              {entry.cv_link && (
                <InfoRow icon={<FileText size={14}/>} label="CV" value={
                  <div style={{ display:'flex', flexDirection:'column', gap:6, alignItems:'flex-start' }}>
                    <a href={entry.cv_link} target="_blank" rel="noreferrer" style={{ color:'#FF4100' }}>Télécharger</a>
                    <CVImportBadge
                      importedAt={cvInfo?.created_at || entry.added_at}
                      importedById={cvInfo?.utilisateur_id || entry.added_by_user_id}
                      importedByName={cvInfo?.nom_proprietaire}
                    />
                  </div>
                }/>
              )}
              {entry.manager_name && <InfoRow icon={<Users size={14}/>} label="Resp. manager" value={entry.manager_name}/>}
              {entry.recruiter_name && <InfoRow icon={<Users size={14}/>} label="Resp. RH" value={entry.recruiter_name}/>}
              {entry.pole && <InfoRow icon={<Users size={14}/>} label="Pôle" value={entry.pole}/>}
              {entry.agency && <InfoRow icon={<MapPin size={14}/>} label="Agence" value={entry.agency}/>}
              {entry.provenance_type && <InfoRow icon={<MessageSquare size={14}/>} label="Provenance" value={[entry.provenance_type, entry.provenance_detail].filter(Boolean).join(' — ')}/>}
              {entry.global_evaluation && <InfoRow icon={<CheckCircle size={14}/>} label="Évaluation globale" value={entry.global_evaluation}/>}
              {entry.boond_ref && <InfoRow icon={<FileText size={14}/>} label="Réf. Boond" value={entry.boond_ref}/>}
              <InfoRow icon={<Calendar size={14}/>} label="Ajouté le" value={entry.added_at ? new Date(entry.added_at).toLocaleDateString('fr-FR', {day:'2-digit',month:'short',year:'numeric'}) : '—'}/>
              <InfoRow icon={<Calendar size={14}/>} label="Dernière MAJ" value={entry.updated_at ? new Date(entry.updated_at).toLocaleDateString('fr-FR', {day:'2-digit',month:'short',year:'numeric'}) : '—'}/>
            </div>
          </div>


          {/* Statut pipeline */}
          <div className="card">
            <div className="card-header"><span className="card-title">Statut pipeline</span></div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, padding:'4px 0' }}>
              <div>
                <label style={lbl}>Statut</label>
                {editing ? (
                  <select className="filter-select" style={{ width:'100%' }} value={form.pipeline_status}
                    onChange={e => setForm(f => ({ ...f, pipeline_status: e.target.value }))}>
                    {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>)}
                  </select>
                ) : (
                  <span style={{ padding:'4px 12px', borderRadius:20, fontSize:'0.8125rem', fontWeight:700, background:`${statusColor}18`, color:statusColor }}>
                    {STATUS_LABELS[entry.pipeline_status] || entry.pipeline_status}
                  </span>
                )}
              </div>
              <div>
                <label style={lbl}>Priorité</label>
                {editing ? (
                  <select className="filter-select" style={{ width:'100%' }} value={form.priority}
                    onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                    {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                ) : (
                  <span style={{ fontWeight:700, color:prioColor }}>{entry.priority}</span>
                )}
              </div>
              <div>
                <label style={lbl}>Source</label>
                <span style={{ fontSize:'0.8125rem', color:'var(--text-muted)' }}>{entry.source_type}</span>
              </div>
              <div>
                <label style={lbl}>Disponibilité</label>
                {editing ? (
                  <input style={inp} value={form.availability || ''} placeholder="ex: Immédiate, 1 mois…"
                    onChange={e => setForm(f => ({ ...f, availability: e.target.value }))}/>
                ) : <span style={{ fontSize:'0.8125rem' }}>{entry.availability || '—'}</span>}
              </div>
              <div>
                <label style={lbl}>TJM</label>
                {editing ? (
                  <input style={inp} value={form.tjm || ''} placeholder="ex: 600€/j"
                    onChange={e => setForm(f => ({ ...f, tjm: e.target.value }))}/>
                ) : <span style={{ fontSize:'0.8125rem', fontWeight:600, color:'#EA580C' }}>{entry.tjm || '—'}</span>}
              </div>
              {entry.score_ao != null && (
                <div>
                  <label style={lbl}>Score AO</label>
                  <span style={{ fontSize:'0.8125rem', fontWeight:700, color:'#FF4100' }}>{Math.round(entry.score_ao)}%</span>
                </div>
              )}
            </div>
          </div>


          {/* Notes */}
          <div className="card">
            <div className="card-header"><span className="card-title">Notes & Retours</span></div>
            <div style={{ display:'flex', flexDirection:'column', gap:12, padding:'4px 0' }}>
              <div>
                <label style={lbl}>Commentaire interne</label>
                {editing ? (
                  <textarea style={{ ...inp, minHeight:80, resize:'vertical' }} value={form.internal_comment || ''}
                    onChange={e => setForm(f => ({ ...f, internal_comment: e.target.value }))}/>
                ) : <p style={{ fontSize:'0.875rem', color: entry.internal_comment ? 'var(--text-primary)' : 'var(--text-muted)', margin:0 }}>
                  {entry.internal_comment || 'Aucun commentaire'}
                </p>}
              </div>
              <div>
                <label style={lbl}>Retour client</label>
                {editing ? (
                  <textarea style={{ ...inp, minHeight:60, resize:'vertical' }} value={form.client_feedback || ''}
                    onChange={e => setForm(f => ({ ...f, client_feedback: e.target.value }))}/>
                ) : <p style={{ fontSize:'0.875rem', color: entry.client_feedback ? 'var(--text-primary)' : 'var(--text-muted)', margin:0 }}>
                  {entry.client_feedback || 'Aucun retour client'}
                </p>}
              </div>
            </div>
          </div>
        </div>


        {/* Colonne droite — Actions */}
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          <div className="card">
            <div className="card-header">
              <span className="card-title"><MessageSquare size={14} style={{ verticalAlign:'middle', marginRight:6 }}/>Actions ({actions.length})</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowActionForm(s => !s)}>
                <Plus size={13}/> Ajouter
              </button>
            </div>


            {/* Formulaire nouvelle action */}
            {showActionForm && (
              <div style={{ padding:'12px 16px', background:'var(--bg-elevated)', borderRadius:8, margin:'0 0 12px', border:'1px solid var(--border)' }}>
                <div style={{ display:'grid', gap:8 }}>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                    <div>
                      <label style={lbl}>Type</label>
                      <select style={{ ...inp }} value={newAction.action_type}
                        onChange={e => setNewAction(f => ({ ...f, action_type: e.target.value }))}>
                        {ACTION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={lbl}>Statut</label>
                      <select style={{ ...inp }} value={newAction.status}
                        onChange={e => setNewAction(f => ({ ...f, status: e.target.value }))}>
                        {ACTION_STATUS.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label style={lbl}>Titre *</label>
                    <input style={inp} placeholder="Titre de l'action" value={newAction.title}
                      onChange={e => setNewAction(f => ({ ...f, title: e.target.value }))}/>
                  </div>
                  <div>
                    <label style={lbl}>Description</label>
                    <textarea style={{ ...inp, minHeight:60, resize:'vertical' }} value={newAction.description}
                      onChange={e => setNewAction(f => ({ ...f, description: e.target.value }))}/>
                  </div>
                  <div>
                    <label style={lbl}>Date échéance</label>
                    <div style={{ display:'flex', gap:5 }}>
                      <input type="date" style={{ ...inp, flex:2 }} value={entDate}
                        onChange={e => setEntDate(e.target.value)} />
                      <select style={{ ...inp, flex:1 }} value={entHour}
                        onChange={e => setEntHour(e.target.value)}>
                        {Array.from({length:24}, (_, i) => String(i).padStart(2,'0')).map(h => (
                          <option key={h} value={h}>{h}h</option>
                        ))}
                      </select>
                      <select style={{ ...inp, flex:1 }} value={entMin}
                        onChange={e => setEntMin(e.target.value)}>
                        {['00','15','30','45'].map(m => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {newAction.action_type === 'ENTRETIEN' && (
                    <>
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                        <div>
                          <label style={lbl}>Durée (minutes) *</label>
                          <select style={inp} value={newAction.duration_minutes}
                            onChange={e => setNewAction(f => ({ ...f, duration_minutes: parseInt(e.target.value) }))}>
                            {[15,30,45,60,90,120].map(d => <option key={d} value={d}>{d} min</option>)}
                          </select>
                        </div>
                        <div style={{ display:'flex', alignItems:'flex-end' }}>
                          <div style={{ padding:'8px 12px', background:'var(--orange-50)', border:'1px solid rgba(255,65,0,0.22)', borderRadius:8, fontSize:'0.75rem', color:'#1D4ED8', display:'flex', alignItems:'center', gap:6, width:'100%' }}>
                            <Video size={13}/> Lien Teams généré automatiquement
                          </div>
                        </div>
                      </div>
                      <div>
                        <label style={lbl}><Users size={11} style={{ verticalAlign:'middle', marginRight:4 }}/>Inviter des membres Wikeys</label>
                        <select style={inp} value=""
                          onChange={e => { if (e.target.value && !invitedUsers.includes(e.target.value)) setInvitedUsers(prev => [...prev, e.target.value]); }}>
                          <option value="">Sélectionner un membre…</option>
                          {allUsers.filter(u => !invitedUsers.includes(u.email) && u.email !== user?.email).map(u => (
                            <option key={u.id} value={u.email}>{u.prenom} {u.nom} — {u.role}</option>
                          ))}
                        </select>
                        {invitedUsers.length > 0 && (
                          <div style={{ display:'flex', flexWrap:'wrap', gap:5, marginTop:6 }}>
                            {invitedUsers.map(email => {
                              const u = allUsers.find(x => x.email === email);
                              return (
                                <span key={email} style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'3px 9px', background:'var(--orange-50)', border:'1px solid rgba(255,65,0,0.22)', borderRadius:20, fontSize:'0.75rem', color:'#1D4ED8', fontWeight:600 }}>
                                  {u ? `${u.prenom || ''} ${u.nom || ''}`.trim() : email}
                                  <button onClick={() => setInvitedUsers(prev => prev.filter(e => e !== email))}
                                    style={{ background:'none', border:'none', cursor:'pointer', padding:0, lineHeight:1, color:'rgba(56,189,248,0.80)', display:'flex', alignItems:'center' }}>
                                    <X size={11}/>
                                  </button>
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <div style={{ padding:'8px 12px', background:'rgba(34,197,94,0.08)', border:'1px solid rgba(34,197,94,0.25)', borderRadius:8, fontSize:'0.75rem', color:'#166534', display:'flex', alignItems:'center', gap:6 }}>
                        <Calendar size={13}/> Ajouté au calendrier WIBusiness à la date de l'entretien · Email d'invitation envoyé au candidat
                      </div>
                    </>
                  )}
                  <div style={{ display:'flex', gap:8, flexWrap:'wrap', justifyContent:'flex-end' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => setShowActionForm(false)}>Annuler</button>
                    <button className="btn btn-primary btn-sm" onClick={handleAddAction}><CheckCircle size={13}/> Enregistrer</button>
                  </div>
                </div>
              </div>
            )}


            {/* Liste actions */}
            {actions.length === 0 && !showActionForm ? (
              <div style={{ padding:'24px 0', textAlign:'center', color:'var(--text-muted)', fontSize:'0.875rem' }}>
                Aucune action enregistrée
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {actions.map(action => {
                  const ac = action.action_type;
                  const sc = action.status === 'FAIT' ? '#16A34A' : action.status === 'ANNULE' ? '#DC2626' : '#F59E0B';
                  return (
                    <div key={action.id} style={{ padding:'10px 12px', border:'1px solid var(--border)', borderRadius:8, position:'relative' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                        <span style={{ fontSize:'0.6875rem', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase' }}>{ac}</span>
                        <span style={{ fontSize:'0.6875rem', fontWeight:700, color:sc }}>{action.status}</span>
                        <button onClick={() => handleDeleteAction(action.id)}
                          style={{ marginLeft:'auto', background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', padding:0 }}>
                          <Trash2 size={12}/>
                        </button>
                      </div>
                      <div style={{ fontWeight:600, fontSize:'0.8125rem', display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                        {action.title}
                        {action.action_type === 'ENTRETIEN' && action.teams_link && (
                          <a href={action.teams_link} target="_blank" rel="noreferrer"
                            style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'2px 10px', borderRadius:10,
                              background:'var(--orange-50)', color:'#FF4100', fontSize:'0.6875rem', fontWeight:700, textDecoration:'none' }}>
                            <Video size={10}/> Rejoindre Teams
                          </a>
                        )}
                        {action.action_type === 'ENTRETIEN' && action.email_sent && (
                          <span style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'2px 8px', borderRadius:10, background:'rgba(34,197,94,0.08)', color:'#16A34A', fontSize:'0.6875rem', fontWeight:700 }}>
                            <Mail size={10}/> Email envoyé
                          </span>
                        )}
                        {action.action_type === 'ENTRETIEN' && !action.teams_link && (
                          <span style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'2px 8px', borderRadius:10, background:'rgba(239,68,68,0.12)', color:'#DC2626', fontSize:'0.6875rem' }}>
                            <AlertTriangle size={10}/> Teams non créé
                          </span>
                        )}
                      </div>
                      {action.description && <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', marginTop:3 }}>{action.description}</div>}
                      {action.due_date && <div style={{ fontSize:'0.6875rem', color:'var(--text-muted)', marginTop:4, display:'flex', alignItems:'center', gap:4 }}>
                        <Calendar size={10}/> {new Date(action.due_date).toLocaleString('fr-FR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit', hour12: false })}
                        {action.duration_minutes && action.action_type === 'ENTRETIEN' && <span>· {action.duration_minutes} min</span>}
                      </div>}
                      <div style={{ fontSize:'0.6875rem', color:'var(--text-muted)', marginTop:4 }}>
                        {new Date(action.created_at).toLocaleDateString('fr-FR')}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          {/* ── Rapports RT (soft/hard skills) ── */}
          {rtReports.length > 0 && (
            <div className="card">
              <div className="card-header">
                <span className="card-title"><CheckCircle size={15} style={{ verticalAlign:'middle', marginRight:6 }}/>Rapports d'évaluation ({rtReports.length})</span>
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {rtReports.map(report => {
                  const typeLabel = report.report_type === 'SOFT_SKILLS' ? 'Soft Skills' : 'Hard Skills';
                  const noteColor = report.note_sur_10 >= 7 ? '#16A34A' : report.note_sur_10 >= 5 ? '#F59E0B' : '#DC2626';
                  const statusBg  = report.status === 'SOUMIS' ? 'rgba(34,197,94,0.08)' : 'rgba(245,158,11,0.08)';
                  const statusColor = report.status === 'SOUMIS' ? '#16A34A' : '#F59E0B';
                  return (
                    <div key={report.id} style={{ padding:'10px 12px', border:'1px solid var(--border)', borderRadius:8 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4, flexWrap:'wrap' }}>
                        <span style={{ fontSize:'0.6875rem', fontWeight:700, color:'#A78BFA', textTransform:'uppercase' }}>{typeLabel}</span>
                        <span style={{ fontSize:'0.6875rem', fontWeight:700, padding:'1px 7px', borderRadius:20, background:statusBg, color:statusColor }}>{report.status}</span>
                        {report.note_sur_10 != null && (
                          <span style={{ fontSize:'0.75rem', fontWeight:800, color:noteColor, marginLeft:'auto' }}>{report.note_sur_10}/10</span>
                        )}
                      </div>
                      {report.rt_user_name && (
                        <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginBottom:4 }}>Par {report.rt_user_name}</div>
                      )}
                      {report.commentaire && (
                        <div style={{ fontSize:'0.8125rem', color:'var(--text-primary)', lineHeight:1.5, marginTop:4 }}>{report.commentaire}</div>
                      )}
                      {Array.isArray(report.table_data) && report.table_data.length > 0 && (
                        <details style={{ marginTop:8 }}>
                          <summary style={{ fontSize:'0.72rem', color:'var(--text-muted)', cursor:'pointer' }}>Voir le tableau ({report.table_data.length} lignes)</summary>
                          <div style={{ marginTop:6, display:'flex', flexDirection:'column', gap:4 }}>
                            {report.table_data.map((row, i) => (
                              <div key={i} style={{ display:'flex', gap:8, fontSize:'0.72rem', padding:'4px 6px', background:'var(--bg-elevated)', borderRadius:5 }}>
                                <span style={{ fontWeight:600, flex:1 }}>{row.critere || row.competence || '—'}</span>
                                {row.note_sur_10 != null && <span style={{ fontWeight:700, color:row.note_sur_10 >= 7 ? '#16A34A' : row.note_sur_10 >= 5 ? '#F59E0B' : '#DC2626' }}>{row.note_sur_10}/10</span>}
                                {row.commentaire && <span style={{ color:'var(--text-muted)', flex:2 }}>{row.commentaire}</span>}
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                      <div style={{ fontSize:'0.6875rem', color:'var(--text-muted)', marginTop:6 }}>
                        {new Date(report.created_at).toLocaleDateString('fr-FR')}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Documents ── */}
          <div className="card">
            <div className="card-header">
              <span className="card-title"><FileText size={15} style={{ verticalAlign:'middle', marginRight:6 }}/>Documents ({documents.length})</span>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                <select style={{ ...inp, width:'auto', padding:'5px 8px', fontSize:'0.75rem', height:32 }}
                  value={docType} onChange={e => setDocType(e.target.value)}>
                  {['CV','CONTRAT','EVALUATION','LETTRE','AUTRE'].map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <label style={{ cursor:'pointer' }} className="btn btn-secondary btn-sm">
                  {uploading
                    ? <><div className="spinner" style={{ width:12, height:12 }}/> Upload…</>
                    : <><Upload size={13}/> Ajouter</>}
                  <input type="file" style={{ display:'none' }} accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                    onChange={handleUploadDoc} disabled={uploading}/>
                </label>
              </div>
            </div>

            {/* CV original depuis la base candidat */}
            {entry.cv_link && !documents.some(d => d.document_type === 'CV') && (
              <div style={{ margin:'0 0 8px', padding:'10px 14px', background:'rgba(255,65,0,0.06)', border:'1px solid rgba(255,65,0,0.20)', borderRadius:8, display:'flex', alignItems:'center', gap:10 }}>
                <FileText size={18} color="#FF4100"/>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:600, fontSize:'0.875rem' }}>CV Original</div>
                  <div style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>CV source du candidat</div>
                </div>
                <a href={entry.cv_link} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">
                  <Download size={12}/> Télécharger
                </a>
              </div>
            )}

            {documents.length === 0 && !entry.cv_link ? (
              <div style={{ textAlign:'center', padding:'20px', color:'var(--text-muted)', fontSize:'0.875rem' }}>
                Aucun document — ajoutez le CV du candidat
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                {documents.map(doc => (
                  <div key={doc.id} style={{ padding:'10px 14px', border:'1px solid var(--border)', borderRadius:8, display:'flex', alignItems:'center', gap:10 }}>
                    <FileText size={18} color="var(--text-muted)"/>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontWeight:600, fontSize:'0.875rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {doc.title || doc.file_name || 'Document'}
                      </div>
                      <div style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>{doc.document_type}</div>
                      {doc.updated_at && (
                        <div style={{ fontSize:'0.68rem', color:'var(--text-muted)', marginTop:2 }}>
                          MAJ : {new Date(doc.updated_at).toLocaleDateString('fr-FR', {day:'2-digit',month:'short',year:'numeric'})}
                        </div>
                      )}
                    </div>
                    {doc.file_url && (
                      <a href={doc.file_url} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">
                        <Download size={12}/> Ouvrir
                      </a>
                    )}
                    <button className="btn btn-ghost btn-icon btn-sm" style={{ color:'var(--danger-600)' }}
                      onClick={() => handleDeleteDoc(doc.id)}>
                      <Trash2 size={12}/>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}


function InfoRow({ icon, label, value }) {
  return (
    <div>
      <label style={{ ...lbl, display:'flex', alignItems:'center', gap:4 }}>{icon}{label}</label>
      <div style={{ fontSize:'0.875rem', color:'var(--text-primary)' }}>{value}</div>
    </div>
  );
}


const lbl = { display:'block', fontSize:'0.75rem', fontWeight:600, color:'var(--text-muted)', marginBottom:4, textTransform:'uppercase', letterSpacing:'0.04em' };
const inp = { width:'100%', padding:'8px 10px', border:'1.5px solid var(--border)', borderRadius:7, fontSize:'0.875rem', boxSizing:'border-box', fontFamily:'inherit', outline:'none', background:'var(--bg-elevated)', color:'var(--text-primary)' };
